// @ts-nocheck
import { Clock, Save, AlertCircle, CheckCircle2, Globe, Calendar } from 'lucide-react'
import { useState, useEffect } from 'react'
import { useApi } from '../../hooks/useApi'
import BusinessHoursToggle from './BusinessHoursToggle'
import TimezoneSelector from './TimezoneSelector'
import WeeklyScheduleEditor from './WeeklyScheduleEditor'
import HolidayManager from './HolidayManager'
import ClosedMessageEditor from './ClosedMessageEditor'
import BusinessHoursPreview from './BusinessHoursPreview'

export default function BusinessHoursSettings({ phoneNumber, onClose, onSave }) {
  const api = useApi()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)
  const [toast, setToast] = useState(null)
  const [refreshKey, setRefreshKey] = useState(0) // プレビュー再取得用

  // 営業時間設定状態
  const [businessHours, setBusinessHours] = useState({
    enabled: false,
    timezone: 'Asia/Tokyo',
    weeklyHours: {
      monday: { isOpen: true, hours: [{ start: '09:00', end: '18:00' }] },
      tuesday: { isOpen: true, hours: [{ start: '09:00', end: '18:00' }] },
      wednesday: { isOpen: true, hours: [{ start: '09:00', end: '18:00' }] },
      thursday: { isOpen: true, hours: [{ start: '09:00', end: '18:00' }] },
      friday: { isOpen: true, hours: [{ start: '09:00', end: '18:00' }] },
      saturday: { isOpen: false, hours: [] },
      sunday: { isOpen: false, hours: [] },
    },
    holidays: [],
    closedMessage: {
      type: 'tts',
      ttsText: '現在、営業時間外です。営業時間は平日9時から18時までです。',
      ttsLanguage: 'ja-JP',
      ttsVoice: 'Polly.Mizuki',
      audioUrl: '',
    },
    closedAction: {
      action: 'hangup',
      voicemailEmail: '',
      forwardNumber: '',
    },
  })

  // 営業時間設定を読み込み
  useEffect(() => {
    const fetchBusinessHours = async () => {
      try {
        setLoading(true)
        setError(null)

        const data = await api.get(`/api/phone-numbers/${phoneNumber.phoneNumberId}/business-hours`)
        if (data.businessHours) {
          setBusinessHours(data.businessHours)
        }
      } catch (err) {
        console.error('営業時間設定取得エラー:', err)
        if (err.message?.includes('404')) {
          // 404の場合はデフォルト値を使用
        } else {
          setError(err.message)
        }
      } finally {
        setLoading(false)
      }
    }

    if (phoneNumber?.phoneNumberId) {
      fetchBusinessHours()
    }
  }, [phoneNumber?.phoneNumberId, api])

  // 営業時間設定を保存
  const handleSave = async () => {
    try {
      setSaving(true)
      setError(null)
      setSuccess(false)

      await api.put(`/api/phone-numbers/${phoneNumber.phoneNumberId}/business-hours`, { businessHours })

      setSuccess(true)
      setHasChanges(false)
      setRefreshKey(prev => prev + 1) // プレビューを強制的に再取得
      
      // 親コンポーネントに保存完了を通知
      if (onSave) {
        onSave()
      }
      
      setToast({ type: 'success', message: '営業時間を保存しました' })
      // トーストのみ3秒後に消す（successフラグは残す）
      setTimeout(() => {
        setToast(null)
      }, 3000)
    } catch (err) {
      console.error('保存エラー:', err)
      setError(err.message)
      setToast({ type: 'error', message: err.message || '保存に失敗しました' })
    } finally {
      setSaving(false)
    }
  }

  // 設定変更時のハンドラー
  const handleChange = (updates) => {
    setBusinessHours((prev) => ({ ...prev, ...updates }))
    setHasChanges(true)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-3"></div>
          <p className="text-gray-500 dark:text-gray-400">読み込み中...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
            <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">営業時間設定</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {phoneNumber.name} ({phoneNumber.phoneNumber})
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        >
          ✕
        </button>
      </div>

      {/* エラーメッセージ */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
          <p className="text-sm font-medium text-red-800 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* 保存成功メッセージ */}
      {success && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4 flex items-center gap-3">
          <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
          <p className="text-sm font-medium text-green-800 dark:text-green-300">
            営業時間設定を保存しました
          </p>
        </div>
      )}

      {/* 営業時間ON/OFF */}
      <BusinessHoursToggle
        enabled={businessHours.enabled}
        onChange={(enabled) => handleChange({ enabled })}
      />

      {businessHours.enabled && (
        <>
          {/* タイムゾーン選択 */}
          <TimezoneSelector
            timezone={businessHours.timezone}
            onChange={(timezone) => handleChange({ timezone })}
          />

          {/* 曜日別営業時間 */}
          <WeeklyScheduleEditor
            weeklyHours={businessHours.weeklyHours}
            onChange={(weeklyHours) => handleChange({ weeklyHours })}
          />

          {/* 祝日管理 */}
          <HolidayManager
            phoneNumberId={phoneNumber.phoneNumberId}
            holidays={businessHours.holidays}
            onChange={(holidays) => handleChange({ holidays })}
          />

          {/* 営業時間外メッセージ */}
          <ClosedMessageEditor
            closedMessage={businessHours.closedMessage}
            closedAction={businessHours.closedAction}
            onChange={(updates) => handleChange(updates)}
          />

          {/* リアルタイムプレビュー */}
          <BusinessHoursPreview
            key={refreshKey}
            phoneNumberId={phoneNumber.phoneNumberId}
            businessHours={businessHours}
          />
        </>
      )}

      {/* 保存ボタン */}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          onClick={onClose}
          className="px-5 py-2.5 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
        >
          キャンセル
        </button>
        <button
          onClick={handleSave}
          disabled={!hasChanges || saving}
          className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          <Save className="w-4 h-4" />
          {saving ? '保存中...' : '保存'}
        </button>
        {success && (
          <button
            type="button"
            onClick={() => {
              setSuccess(false)
              onClose()
            }}
            className="px-4 py-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-200 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
          >
            閉じる
          </button>
        )}
      </div>

      {/* Toast通知 */}
      {toast && (
        <div className="fixed bottom-4 right-4 z-50">
          <div
            className={`rounded-lg shadow-lg px-4 py-3 text-sm font-medium flex items-center gap-2 ${
              toast.type === 'success'
                ? 'bg-green-600 text-white'
                : 'bg-red-600 text-white'
            }`}
          >
            {toast.type === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
            <span>{toast.message}</span>
          </div>
        </div>
      )}
    </div>
  )
}
